import { computed, onScopeDispose, watch } from 'vue'
import { useStore } from 'vuex'
import axiosInstance from '@/axios'
import { useProductEditorStore } from '@/stores/productEditor'
import {
    useProductEditorUiState,
    LEFT_RAIL_ITEMS,
} from '@/stores/productEditorUiState'
import { useImpersonationStore } from '@/stores/impersonation'
import { useStorefrontColorRendersStore } from '@/stores/storefrontColorRenders'
import {
    useArtifiPreviewCacheStore,
    buildCacheKey,
} from '@/stores/artifiPreviewCache'
import { requestArtifiPreview } from '@/composables/useArtifiPreview'
import {
    COLORWAY_PENDING_RETRY_MS,
    COLORWAY_STATE,
    previewColorwayArgs,
} from '@/utils/previewColorway'
import {
    useArtifiCoverage,
    normalizeColorKey,
} from '@/composables/useArtifiCoverage'
import { useArtifiProductViews } from '@/composables/useArtifiProductViews'
import { useVariantResolver } from '@/composables/useVariantResolver'
import { useColorRenderDebounce } from '@/composables/useColorRenderDebounce'
import { artifiPreviewEligible } from '@/utils/supplier-entitlement'
import { includeColorRender } from '@/utils/storefrontImages'
import { droppedPlacementCodes } from '@/components/decoration/previewWarningCopy'
import {
    resolveViewCodeForSku,
    strictViewCodeForSku,
    isZoneValidForSku,
    frontGroupCodesForSku,
    backGroupCodesForSku,
} from '@/utils/placementZoneToViewCode'

/**
 * useStorefrontColorRenders -- a decorated render for EVERY selected color
 * of the product being edited (Jake 2026-10-06, card
 * https://trello.com/c/Qlwx8mlT). Mounted ONCE by ProductEditor.vue; the
 * Storefront listing panel reads the results from the
 * storefrontColorRenders store.
 *
 * TRIGGER POLICY (Jake 2026-10-07). This replaces the OLD policy of #4071
 * (superseded, no longer in force: it rendered a color a fixed 15 seconds
 * after it was SELECTED). The current policy is:
 *
 *   - ON SAVE, DEBOUNCED: nothing renders until the render delay has passed
 *     since the product was last SAVED (editor.saveCount -- a field autosave
 *     or an explicit Save). The delay is the Super Admin platform setting
 *     color_render_debounce_seconds (default 5s; Jake 2026-10-28, was a
 *     fixed 15s), read through useColorRenderDebounce when the wait is armed.
 *     Each further save restarts the wait. When it fires, every SELECTED
 *     color that lacks a current render renders, under the cap. This lets a
 *     distributor toggle colors on and off freely before anything is spent.
 *   - LEAVING FOR PRODUCT LISTING OR PLACEMENTS, IMMEDIATELY (Jake
 *     2026-10-07): entering either page runs a pending save debounce NOW
 *     instead of waiting it out -- the wait only makes sense while the
 *     distributor is still on Sizes & Colors. An edit whose autosave has not
 *     landed yet renders the moment it saves. Entering with no save pending
 *     renders nothing (opening a product is still not a save).
 *   - A PLACEMENT ADDED BY THE WIZARD (Bug Backlog #356) counts as a save:
 *     its own POST never moved saveCount, so nothing rendered. It renders
 *     now on Product Listing or a Placements page (where the wizard closes)
 *     and after the debounce on Sizes & Colors.
 *   - DELIBERATE PICK, IMMEDIATELY: clicking a color in "Colors on this
 *     product" (on Sizes & Colors or a Placements page) renders that ONE
 *     color now.
 *   - TICK RENDERS THE COLOR NOW (Jake 2026-10-28): ticking a color on Sizes
 *     & Colors moves the Stage to it and renders that ONE color at once, by
 *     the same path and spend guards as a pick (productEditorUiState
 *     colorTickCount). The save debounce above still serves the colors the
 *     distributor is NOT looking at. Unticking renders nothing. The landed
 *     render of an unsaved tick waits for the save before it is recorded
 *     (see RECORDED FOR THE PRODUCT PAGE).
 *   - "RENDER ALL COLORS", IMMEDIATELY: the explicit backfill -- every
 *     selected color without a current render, now.
 *   - OPENING A PRODUCT RENDERS NOTHING AND WRITES NOTHING. Opening is not a
 *     save. A color the server already holds a CURRENT render for (its
 *     recorded decorated_preview pointer carries this color's wake identity)
 *     is current from the start and never re-rendered.
 *   - SYSTEM PICKS DO NOT RENDER: a color the SYSTEM selected (the default
 *     color picked for a NEW product at SKU setup) is skipped by the save
 *     debounce until the distributor acts on it; a pick or "Render all
 *     colors" renders it. The default a CONFIRMED SKU SWAP picks is NOT
 *     a system pick (Jake 2026-10-22, card #338): the distributor confirmed
 *     the swap, so it renders once through the save debounce above.
 *   - HONEST STAND-IN (#338): while a color's render is pending the Stage
 *     shows its supplier photo with a "Rendering..." indicator; a scheduled
 *     color says "Preview renders shortly" (storefrontColorRenders
 *     standInState); a color that could render with nothing coming gets a
 *     "Render preview" action that calls onStageColor.
 *   - STALENESS: when a color's wake identity changes (its artwork or
 *     colorway), it needs a render again and goes through the same
 *     triggers. Wake identity, not the cache key, decides this -- see
 *     wakeIdentityFor.
 *   - NO PLACEMENT, NO CALL: argsFor returns null without a placement, and
 *     requestArtifiPreview -- the one request chokepoint -- refuses a request
 *     without placementCount >= 1 regardless of caller.
 *   - SPEND GUARDS (unchanged): never re-request what artifiPreviewCache
 *     already holds (a hit is served at once, no call); at most
 *     MAX_CONCURRENT_RENDERS in flight, the rest queue; requests share the
 *     Stage's in-flight de-dup, and the backend's durable preview cache
 *     answers a repeat without Artifi.
 *   - FAIL TOWARD THE RECORD (unchanged): no bound artwork, an unrouted
 *     color, no live view for the placement's zone, a zone the SKU has no
 *     camera for (#2874), or a SuperAdmin without impersonation -> no args,
 *     no call, no tile. A failed render -> no tile, never a broken one.
 *   - DEFAULT-INCLUDE (unchanged): a color's render is added to Chosen
 *     (storefront_images[color]) when it lands, unless the distributor has
 *     already curated that color (the key exists) -- then it goes to the
 *     Available pool only. Chosen images drive the product CARD carousel.
 *   - RECORDED FOR THE PRODUCT PAGE: every render that lands is recorded
 *     server-side (POST /api/campaign_products/<id>/color_renders) as the
 *     color's decorated_preview pointer. The product page shows it for a
 *     color with no chosen image. No Artifi call -- the render already exists.
 *     The server refuses a record for a color it does not hold as selected
 *     (422 color_not_selected), so a render that lands while ANY edit to the
 *     selection is unsaved or on the wire waits for that save, then records
 *     if the persisted selection in serverState holds its color. A refusal anyway (the
 *     server disagreed) is retried after the next save, MAX_RECORD_RETRIES
 *     times, then dropped silently: the browser already logged the 4xx, and
 *     the next render of the color records again. At most one record per
 *     color is on the wire, and a wake already recorded is never re-sent.
 *
 * PARTIAL RENDERS (#312). The composite answers 200 with the placements it
 * COULD draw plus a `placement_<reason>:<zone>` warning for each it dropped
 * (an artwork whose preview is still pending, an SVG registered before the
 * rasterizer, an EPS). Such an image is NOT a finished render: it is held back
 * -- never chosen for the storefront, never recorded as current, never put in
 * the shared preview cache -- exactly as the server's M7 writer refuses to
 * persist it (decorated_preview_render.composite_dropped_a_placement). The
 * color's state is 'partial' with the dropped codes (the panel says why), it
 * retries on its own a bounded number of times, and "Render all colors"
 * offers it again.
 *
 * Contrast with the server-side spend trigger
 * (api/src/services/colorway_render_trigger.py): that one renders at the
 * moment a write makes a render NECESSARY. This surface renders ahead of
 * need, so the debounce is what keeps it cheap.
 *
 * RENDER IDENTITY. Each color renders exactly what the Stage shows for that
 * color on a fresh load: the FIRST placement on its own view (no selected
 * placement, no Front/Back override), compositing every placement on that
 * view's group when there is more than one (which is also the path the
 * backend routes to the LOCAL renderer for pseudo-supplier SKUs --
 * api/src/services/preview_render_routing.py). Deliberately NOT the editor's
 * current focus: renders are chosen by default now, so flipping the Stage to
 * the back view must not re-render every color and push back shots into
 * the listing. The args shape (and so the cache key) mirrors Stage.vue's
 * cacheArgs, so the Stage and the listing share cache entries.
 */

// Renders run after the SA-set delay (useColorRenderDebounce) following the
// product's LAST save, while the distributor stays on Sizes & Colors;
// entering Product Listing or a Placements page runs them at once.
export const MAX_CONCURRENT_RENDERS = 2

// A composite that DROPPED a placement (#312) is retried on its own: the usual
// cause is an artwork whose preview is still being made, which is ready within
// seconds to a minute or two. The retry is cheap -- every placement that did
// render is cached on the server per call, and a dropped one makes no vendor
// call at all -- and bounded, so a placement that can never render (an EPS)
// stops asking after MAX_PARTIAL_RETRIES. After that only a trigger renders it
// again ("Render all colors", the next save, a Stage pick).
// The render RECIPE version, the leading part of every wake identity this
// composable records. Bump it when what a render depicts changes in a way an
// old record cannot be trusted for: a record written under an older recipe
// then reads as "rendered the old way" and is re-rendered once (#312: renders
// recorded before partial composites were detected may be missing a
// placement). No server change: the wake is an opaque string to the API.
export const WAKE_RECIPE = 'r2'

export const PARTIAL_RETRY_MS = 20_000
export const MAX_PARTIAL_RETRIES = 6
// ...and across ALL colors of a product in a session, so a product with many
// unrenderable colors cannot fan out into many preview calls.
export const MAX_PARTIAL_RETRIES_TOTAL = 12

// A record the server refused with color_not_selected is retried after the
// next save settles, this many times, then given up (see recordRender).
export const MAX_RECORD_RETRIES = 2

function zoneOf(placement) {
    return (
        placement?.placement_zone_slug ||
        placement?.placement ||
        placement?.zone ||
        placement?.zone_code ||
        null
    )
}

// The editor pages where a waiting render is wanted NOW: Product Listing
// (LEFT_RAIL_ITEMS.STOREFRONT) and any placement (`placement:<id>`).
function isRenderNowPage(item) {
    return (
        item === LEFT_RAIL_ITEMS.STOREFRONT ||
        String(item ?? '').startsWith('placement:')
    )
}

function isHttpUrl(url) {
    return typeof url === 'string' && /^https?:\/\//i.test(url)
}

// The record endpoint's "that color is not (yet) selected on the server".
function isColorNotSelected(err) {
    return (
        err?.response?.status === 422 &&
        err?.response?.data?.error?.code === 'color_not_selected'
    )
}

export function useStorefrontColorRenders() {
    const editor = useProductEditorStore()
    const renderStore = useStorefrontColorRendersStore()
    const cache = useArtifiPreviewCacheStore()
    const vuexStore = useStore()
    const impersonation = useImpersonationStore()
    const editorUi = useProductEditorUiState()
    const { resolve: resolveVariantForColor } = useVariantResolver()

    const productItemNumber = computed(
        () => editor.getEffectiveAttribute?.('product_item_number') ?? null,
    )

    // Same supplier gate as Stage / the Colors panel: only suppliers whose
    // styles Artifi carries (SanMar, S&S) have views + coverage behind these
    // endpoints. See artifiPreviewEligible.
    const artifiSku = computed(() =>
        artifiPreviewEligible({
            supplierKey: editor.supplierKey,
            supplierKeyResolved: editor.supplierKeyResolved,
            supplierName: editor.getEffectiveAttribute?.('supplier_name'),
        })
            ? productItemNumber.value
            : null,
    )
    const { views, loading: viewsLoading } = useArtifiProductViews(artifiSku)
    const {
        supportedWithVariantSku,
        styleUnsupported,
        loading: coverageLoading,
    } = useArtifiCoverage(artifiSku)

    // Mirrors productEditorUiState.setSkuViews: an empty list is "no
    // authority", which the view resolvers express as null.
    const skuViews = computed(() =>
        Array.isArray(views.value) && views.value.length ? views.value : null,
    )

    const isSuperAdminWithoutImpersonation = computed(
        () =>
            Boolean(vuexStore?.getters?.isSuperAdmin) && !impersonation.isActive,
    )

    const campaignProductId = computed(() => {
        const raw = editor.serverState?.data?.id
        if (raw == null) return null
        const n = Number(raw)
        return Number.isFinite(n) ? n : null
    })

    // The color skus the SERVER holds as selected -- known only while no
    // selection edit is unsaved or on the wire; null until then. serverState
    // advances only when a save lands (saveDirtyFields / save), so a color
    // ticked but not yet autosaved is absent from it. And while an edit to
    // selected_color_skus sits in the dirty Map (it stays there until its
    // PATCH succeeds) serverState may still list a color that edit is about
    // to DROP: Remove all autosaved alone, the re-tick waiting behind it
    // (gate round 2, 2026-10-24). The record endpoint checks exactly this.
    const savedSelection = computed(() => {
        if (editor.dirty?.has?.('selected_color_skus')) return null
        const raw = editor.serverState?.data?.attributes?.selected_color_skus
        return new Set(Array.isArray(raw) ? raw : [])
    })

    // Selected colors, resolved to {color_sku, name} exactly as the listing
    // panel's pickerColors does -- storefront_images is keyed by NAME.
    const selectedColors = computed(() => {
        const raw = editor.getEffectiveAttribute?.('selected_color_skus') ?? []
        const skus = [...new Set(Array.isArray(raw) ? raw : [])]
        const bySku = new Map(
            (editor.supplierMeta?.colors ?? []).map((c) => [c.color_sku, c]),
        )
        return skus.map((sku) => ({
            color_sku: sku,
            name: bySku.get(sku)?.name ?? sku,
        }))
    })

    // Stage.vue's variantBindings, verbatim in meaning: what each variant
    // puts on the garment (artwork + colorway). It is the cache key's
    // invalidation input, so it is also what makes a rebind re-render.
    const variantBindings = computed(() => {
        const out = {}
        for (const deco of editor.decorations ?? []) {
            for (const v of deco.variants ?? []) {
                const k = v?.id ?? v?._tempId ?? null
                if (k != null) {
                    out[String(k)] = {
                        artworkFileId: v.artwork_file_id ?? null,
                        colorwayId: v.colorway_id ?? null,
                    }
                }
            }
        }
        return out
    })

    function groupSideForView(view) {
        if (!view) return null
        if (frontGroupCodesForSku(skuViews.value).has(view)) return 'front'
        if (backGroupCodesForSku(skuViews.value).has(view)) return 'back'
        return null
    }

    function clientVariantSku(colorSku) {
        const map = supportedWithVariantSku.value || {}
        if (Object.prototype.hasOwnProperty.call(map, colorSku)) return map[colorSku]
        const wanted = normalizeColorKey(colorSku)
        if (!wanted) return null
        for (const [k, v] of Object.entries(map)) {
            if (normalizeColorKey(k) === wanted) return v
        }
        return null
    }

    // The render args for one color, or null when it must not render.
    function argsFor(color) {
        if (isSuperAdminWithoutImpersonation.value) return null
        // Wait for the SKU's live views: rendering on a provisional view code
        // and re-rendering when the real one lands would double the spend.
        if (viewsLoading.value) return null
        // Card #243: Artifi can render NO color of this style (it does not
        // know the SKU, or no color resolves). Every call would fail, so make
        // none; the distributor's own image is the listing preview instead.
        if (styleUnsupported?.value) return null
        // ...and until coverage answers we cannot know it can.
        if (coverageLoading?.value) return null
        const decorations = editor.decorations ?? []
        const first = decorations[0]
        if (!first) return null
        // #2874: any placement on a zone this SKU has no camera for makes the
        // render a garment with that decoration silently missing. The Stage
        // approximates instead of calling Artifi; so do we (by not calling).
        if (
            decorations.some(
                (p) => zoneOf(p) && !isZoneValidForSku(zoneOf(p), skuViews.value),
            )
        ) {
            return null
        }
        const zone = zoneOf(first)
        const viewCode = resolveViewCodeForSku({
            zoneCode: zone,
            skuViews: skuViews.value,
        })
        if (!viewCode) return null
        if (zone && !strictViewCodeForSku(zone, skuViews.value)) return null

        const variant = resolveVariantForColor(
            first.id ?? first._tempId,
            color.color_sku,
        )
        const artworkFileId = variant?.artwork_file_id ?? null
        if (!artworkFileId) return null

        const side = groupSideForView(viewCode)
        const groupCount = side
            ? decorations.filter((p) => {
                  const v = resolveViewCodeForSku({
                      zoneCode: zoneOf(p),
                      skuViews: skuViews.value,
                  })
                  return groupSideForView(v) === side
              }).length
            : 0
        const useComposite = groupCount > 1 && campaignProductId.value != null

        return {
            productSku: productItemNumber.value,
            colorSku: color.color_sku,
            viewCode,
            campaignProductId: useComposite ? campaignProductId.value : null,
            artworkFileId: useComposite ? null : artworkFileId,
            // The same placement + colorway identity the Stage names for this
            // request (the server derives the colorway from the placement), so
            // the two share cache keys and in-flight requests.
            ...previewColorwayArgs({
                placement: first,
                variant,
                artworkFileId: useComposite ? null : artworkFileId,
                composite: useComposite,
            }),
            variantSku: clientVariantSku(color.color_sku) || color.color_sku,
            masterSku: productItemNumber.value,
            colorName: color.color_sku,
            variantBindings: variantBindings.value,
            placementCount: decorations.length,
            // How many placements the request composites: what a clean
            // render of this color draws. Not part of the cache key.
            groupPlacementCount: useComposite ? groupCount : 1,
        }
    }

    // A color's WAKE identity (#4071 gate P1-1): the artwork + colorway of
    // every variant routed to THAT color, across all placements, sorted.
    // Deliberately NOT the cache key: the key folds in every variant on the
    // product and their row ids, so another color's rebind, a placement that
    // routes nothing new to this color, or Save swapping temp ids for server
    // ids would all move it -- and a moved key is not an edit to this color.
    // Only a change here starts a render for an already-present color.
    function wakeIdentityFor(color) {
        const parts = []
        for (const placement of editor.decorations ?? []) {
            const v = resolveVariantForColor(
                placement.id ?? placement._tempId,
                color.color_sku,
            )
            if (v) parts.push(`${v.artwork_file_id ?? ''}@${v.colorway_id ?? ''}`)
        }
        return `${WAKE_RECIPE}:${parts.sort().join('|')}`
    }

    // color NAME -> { args, key, wake, sku } for every selected color that
    // can render. `key` is the cache key (lookup only); `wake` decides
    // whether a change is this color's edit.
    const desired = computed(() => {
        const out = {}
        for (const color of selectedColors.value) {
            const args = argsFor(color)
            if (args) {
                out[color.name] = {
                    args,
                    key: buildCacheKey(args),
                    wake: wakeIdentityFor(color),
                    sku: color.color_sku,
                }
            }
        }
        return out
    })

    // ---- per-color lifecycle (plain, non-reactive bookkeeping) ----
    //
    // entry: { key, wake, sku, status, url, prevUrl }, status one of
    //   'needed'  -- selected, renderable, no current render. Waits for a
    //                trigger: the save debounce, a Stage switch to it, or
    //                "Render all colors".
    //   'queued' | 'loading' -- a render is on its way.
    //   'ready'   -- current. Either rendered (or served from cache) in this
    //                session, or recorded on the server for this wake.
    //   'failed'  -- the last attempt failed; offered again by every trigger.
    // prevUrl carries the color's last READY url across a wake change, so the
    // regenerated render can replace it in Chosen.
    //
    // autoPicked: color skus the SYSTEM selected (the SKU-setup default
    // color). The save debounce skips them until the distributor acts on the
    // color (deselects it, or a Stage switch / Render all renders it).
    const entries = new Map()
    let queue = []
    let inFlight = 0
    let disposed = false
    let productId = null
    // The SA-set delay; read when a wait is armed, so a change applies to
    // the next save.
    const renderDebounce = useColorRenderDebounce()
    let saveTimer = null
    let partialRetryTimer = null
    let partialRetriesUsed = 0
    // Set on entering a render-now page while an edit's autosave is still
    // pending: that save then runs the debounce's work at once.
    let flushOnNextSave = false
    let autoPicked = new Set()
    // color sku -> wake identity the server holds a pointer for: from the
    // loaded product, then from each successful record in this session. It
    // de-duplicates records. Only the LOADED pointers (serverWake) make a
    // color current on sight -- a color re-selected in this session needs a
    // trigger again, which a cache hit then serves for free.
    let recordedWake = new Map()
    let serverWake = new Map()
    // color skus already re-rendered because their record predates the
    // current recipe: once per color per session, whatever the outcome.
    let legacyRerendered = new Set()
    // color sku -> { name, entry, url, retries, afterSave }: a landed render
    // whose record waits for its color's selection to be saved, or (after a
    // color_not_selected refusal, afterSave) for the next save to land.
    const pendingRecords = new Map()
    // color sku -> the wake of the record POST on the wire for it.
    let recordingWake = new Map()

    function clearSaveTimer() {
        if (saveTimer) clearTimeout(saveTimer)
        saveTimer = null
    }

    function clearPartialRetryTimer() {
        if (partialRetryTimer) clearTimeout(partialRetryTimer)
        partialRetryTimer = null
    }

    function clearEntry(name) {
        const e = entries.get(name)
        if (e && pendingRecords.get(e.sku)?.entry === e) pendingRecords.delete(e.sku)
        entries.delete(name)
        renderStore.dropRender(name)
    }

    function resetAll() {
        for (const name of [...entries.keys()]) clearEntry(name)
        pendingRecords.clear()
        queue = []
        clearSaveTimer()
        clearPartialRetryTimer()
        renderStore.reset()
    }

    // The pointers the loaded product already carries: decorated_preview
    // {sku: {F: {digest, wake, ...}}}. A pointer recorded by the editor
    // carries the color's wake identity; one without it (M7's server writer)
    // never counts as current here, so it is simply re-rendered on demand.
    function loadRecordedPointers() {
        const out = new Map()
        const map = editor.getEffectiveAttribute?.('decorated_preview')
        if (map && typeof map === 'object') {
            for (const [sku, views] of Object.entries(map)) {
                const entry = views && typeof views === 'object' ? views.F : null
                if (entry && typeof entry.wake === 'string' && typeof entry.digest === 'string') {
                    out.set(sku, { wake: entry.wake, digest: entry.digest })
                }
            }
        }
        return out
    }

    function needsRender(entry) {
        return (
            entry &&
            (entry.status === 'needed' ||
                entry.status === 'failed' ||
                entry.status === 'partial')
        )
    }

    // Selected colors that could render but have no current render and none
    // on its way -- what the backfill button offers.
    function publishMissing() {
        const missing = []
        for (const [name, e] of entries) {
            if (needsRender(e)) missing.push(name)
        }
        renderStore.setMissingColors(missing)
        // A color is SCHEDULED when an existing trigger will start it: a save
        // debounce is armed, or an edit's autosave has not landed (its save
        // arms the debounce). A system pick is never started by those.
        const autosave = editor.fieldAutoSaveState
        const trigger =
            saveTimer != null ||
            flushOnNextSave ||
            autosave === 'dirty' ||
            autosave === 'saving'
        renderStore.setScheduledColors(
            trigger
                ? missing.filter((n) => !autoPicked.has(entries.get(n)?.sku))
                : [],
        )
    }

    function applyInclude(name, url, prevUrl) {
        if (!isHttpUrl(url)) return // the validator only accepts http(s)
        const current = editor.getEffectiveAttribute?.('storefront_images') ?? {}
        const next = includeColorRender(current, name, url, {
            replacesUrl: prevUrl,
        })
        if (next !== current) editor.updateField('storefront_images', next)
    }

    // Record the landed render as the color's decorated_preview pointer, so
    // the product page shows it for a color with no chosen image. Best
    // effort: a failed record leaves the product page on its blank for that
    // color, and the next render of the color records again. Never sent
    // before the server holds the color as selected (see flushRecords).
    function recordRender(name, entry, url) {
        if (campaignProductId.value == null || !isHttpUrl(url)) return
        if (recordedWake.get(entry.sku) === entry.wake) return
        const queued = pendingRecords.get(entry.sku)
        // The same render already waiting keeps its retry count.
        if (!queued || queued.entry !== entry) {
            pendingRecords.set(entry.sku, { name, entry, url, retries: 0, afterSave: false })
        }
        flushRecords()
    }

    // Send every waiting record whose color the server now holds as selected.
    // `saved`: a save just landed, which releases records waiting on a retry.
    function flushRecords({ saved = false } = {}) {
        if (disposed) return
        for (const [sku, rec] of [...pendingRecords]) {
            // Deselected, re-identified, or a different product: drop it.
            if (entries.get(rec.name) !== rec.entry) {
                pendingRecords.delete(sku)
                continue
            }
            if (recordedWake.get(sku) === rec.entry.wake) {
                pendingRecords.delete(sku)
                continue
            }
            if (rec.afterSave) {
                if (!saved) continue
                rec.afterSave = false
            }
            if (!savedSelection.value?.has(sku)) continue
            // One record per color on the wire; this one re-checks when it
            // settles.
            if (recordingWake.has(sku)) continue
            pendingRecords.delete(sku)
            postRecord(rec)
        }
    }

    function postRecord(rec) {
        const { entry, url } = rec
        const { sku, wake } = entry
        const cpId = campaignProductId.value
        const wire = recordingWake
        wire.set(sku, wake)
        axiosInstance
            .post(`/api/campaign_products/${cpId}/color_renders`, {
                color: sku,
                url,
                wake,
            })
            .then(() => {
                if (!disposed && productId === cpId) recordedWake.set(sku, wake)
            })
            .catch((err) => {
                if (disposed || productId !== cpId) return
                if (isColorNotSelected(err)) {
                    // The server does not hold the color as selected after
                    // all. Try again once the next save lands -- unless the
                    // color is gone (reconcile dropped it) or a newer render
                    // is already waiting. Silent: the browser logged the 4xx.
                    if (
                        rec.retries < MAX_RECORD_RETRIES &&
                        entries.get(rec.name) === entry &&
                        !pendingRecords.has(sku)
                    ) {
                        pendingRecords.set(sku, {
                            ...rec,
                            retries: rec.retries + 1,
                            afterSave: true,
                        })
                    }
                    return
                }
                console.warn(
                    `[useStorefrontColorRenders] could not record the ${sku} render:`,
                    err?.message ?? err,
                )
            })
            .finally(() => {
                if (wire.get(sku) === wake) wire.delete(sku)
                if (!disposed && productId === cpId) flushRecords()
            })
    }

    function markReady(name, entry, url, placements = null) {
        entry.status = 'ready'
        entry.url = url
        renderStore.setRender(name, { status: 'ready', url, placements })
        applyInclude(name, url, entry.prevUrl)
        recordRender(name, entry, url)
        entry.prevUrl = null
        publishMissing()
    }

    function land(name, entry, url, dropped = []) {
        if (disposed) return
        // Deselected, re-identified, or the product changed while in flight:
        // the url is cached (above) but belongs to nothing on screen now.
        if (entries.get(name) !== entry) return
        if (url && dropped.length > 0) {
            // Missing a placement it should show: not a finished render.
            entry.status = 'partial'
            entry.url = null
            entry.partialTries = (entry.partialTries ?? 0) + 1
            renderStore.setRender(name, { status: 'partial', url: null, dropped })
            publishMissing()
            schedulePartialRetry()
        } else if (url) {
            markReady(name, entry, url, entry.placements)
        } else {
            entry.status = 'failed'
            entry.url = null
            renderStore.setRender(name, { status: 'failed', url: null })
            publishMissing()
        }
    }

    // cache key -> the request on the wire for it. A color unticked and
    // re-ticked while its render is in flight gets a NEW entry with the same
    // key; it joins the first request instead of spending a second render.
    const requestsByKey = new Map()

    function requestOnce(key, args) {
        let p = requestsByKey.get(key)
        if (!p) {
            p = requestArtifiPreview(args)
            requestsByKey.set(key, p)
            const done = () => {
                if (requestsByKey.get(key) === p) requestsByKey.delete(key)
            }
            p.then(done, done)
        }
        return p
    }

    function start(name, entry, args) {
        const key = entry.key
        entry.status = 'loading'
        inFlight += 1
        requestOnce(key, args)
            .then((resp) => {
                const url = resp?.data?.url ?? null
                const dropped = droppedPlacementCodes(resp?.data?.warnings)
                const colorwayState = resp?.data?.colorway?.state ?? null
                if (url && colorwayState === COLORWAY_STATE.PENDING) {
                    // The bound colorway's asset is being created: this image
                    // is the DEFAULT colors. Not cached, not recorded as the
                    // color's storefront render -- ask again shortly.
                    retryPendingColorway(name, entry)
                    return
                }
                // A partial composite is never cached: the shared cache would
                // serve it as the finished render on the next visit. Neither
                // is a colorway that could not be applied (`unavailable`): the
                // best image there is is the original colors, kept for this
                // color but never served from cache as the colorway's render.
                if (
                    url &&
                    dropped.length === 0 &&
                    colorwayState !== COLORWAY_STATE.UNAVAILABLE
                ) {
                    cache.set(key, url)
                }
                land(name, entry, url, dropped)
            })
            .catch(() => land(name, entry, null))
            .finally(() => {
                inFlight -= 1
                pump()
            })
    }

    // Re-request a color whose colorway is still `pending`, on the bounded
    // COLORWAY_PENDING_RETRY_MS schedule; after the last attempt the color
    // counts as failed (offered again by "Render all colors"), never recorded.
    function retryPendingColorway(name, entry) {
        const attempt = entry.pendingTries ?? 0
        if (attempt >= COLORWAY_PENDING_RETRY_MS.length) {
            land(name, entry, null)
            return
        }
        entry.pendingTries = attempt + 1
        setTimeout(() => {
            if (disposed || entries.get(name) !== entry) return
            entry.status = 'queued'
            queue.push(name)
            pump()
        }, COLORWAY_PENDING_RETRY_MS[attempt])
    }

    function pump() {
        if (disposed) return
        while (inFlight < MAX_CONCURRENT_RENDERS && queue.length) {
            const name = queue.shift()
            const e = entries.get(name)
            if (!e || e.status !== 'queued') continue
            const d = desired.value[name]
            if (!d || d.wake !== e.wake) continue
            if (cache.has(e.key)) {
                markReady(name, e, cache.get(e.key))
                continue
            }
            start(name, e, d.args)
        }
    }

    // Queue one color that needs a render. A cache hit is served at once and
    // never takes a slot. `quiet` (the partial retry) leaves the color's
    // published state alone, so the "missing a placement" note does not blink
    // out and back every retry.
    function enqueue(name, { quiet = false } = {}) {
        const entry = entries.get(name)
        if (!needsRender(entry)) return
        if (cache.has(entry.key)) {
            markReady(name, entry, cache.get(entry.key), entry.placements ?? null)
            return
        }
        entry.status = 'queued'
        // A fresh request for this color (save, pick, "Render all colors"):
        // its pending-colorway retries start over.
        entry.pendingTries = 0
        if (!quiet) renderStore.setRender(name, { status: 'pending', url: null })
        queue.push(name)
    }

    // Retry the colors whose last composite dropped a placement, once, after
    // PARTIAL_RETRY_MS -- unless they have used up their attempts.
    function schedulePartialRetry() {
        if (partialRetryTimer || disposed) return
        if (partialRetriesUsed >= MAX_PARTIAL_RETRIES_TOTAL) return
        const due = [...entries.values()].some(
            (e) => e.status === 'partial' && (e.partialTries ?? 0) <= MAX_PARTIAL_RETRIES,
        )
        if (!due) return
        partialRetryTimer = setTimeout(() => {
            partialRetryTimer = null
            if (disposed) return
            for (const [name, e] of entries) {
                if (
                    e.status === 'partial' &&
                    (e.partialTries ?? 0) <= MAX_PARTIAL_RETRIES &&
                    partialRetriesUsed < MAX_PARTIAL_RETRIES_TOTAL
                ) {
                    partialRetriesUsed += 1
                    enqueue(name, { quiet: true })
                }
            }
            pump()
        }, PARTIAL_RETRY_MS)
    }

    // TRIGGER 1: the render delay after the last save (or at once, see below), every
    // selected color that needs a render -- except system picks the
    // distributor has not acted on.
    function onSaveDebounceElapsed() {
        saveTimer = null
        if (disposed) return
        for (const [name, e] of entries) {
            if (autoPicked.has(e.sku)) continue
            enqueue(name)
        }
        pump()
        publishMissing()
    }

    function onSaved() {
        if (disposed) return
        clearSaveTimer()
        if (flushOnNextSave && isRenderNowPage(editorUi.selectedLeftRailItem)) {
            flushOnNextSave = false
            onSaveDebounceElapsed()
            return
        }
        saveTimer = setTimeout(onSaveDebounceElapsed, renderDebounce.debounceMs.value)
        publishMissing()
    }

    // TRIGGER 1 for a placement the Add-placement wizard created (Bug
    // Backlog #356). The wizard saves it with its own POST, so saveCount
    // never moves; it is a save all the same. On Product Listing or a
    // Placements page -- where the wizard closes -- the render runs now, as
    // entering those pages would; on Sizes & Colors it waits the debounce
    // like any other save.
    function onPlacementSaved() {
        if (disposed) return
        reconcile(desired.value)
        if (isRenderNowPage(editorUi.selectedLeftRailItem)) {
            clearSaveTimer()
            onSaveDebounceElapsed()
            return
        }
        onSaved()
    }

    // TRIGGER 1, flushed: entering Product Listing or a Placements page runs
    // a pending save debounce now. The same work as the timer -- the same
    // entries, cache hits, cap and queue -- so a color already current or on
    // its way is never requested twice, and with no save pending nothing
    // runs. An edit still waiting on its autosave renders when that lands.
    function onRailItem(item) {
        if (disposed) return
        if (!isRenderNowPage(item)) {
            flushOnNextSave = false
            return
        }
        if (saveTimer) {
            clearSaveTimer()
            onSaveDebounceElapsed()
            return
        }
        const autosave = editor.fieldAutoSaveState
        flushOnNextSave = autosave === 'dirty' || autosave === 'saving'
        publishMissing()
    }

    // TRIGGER 2: a deliberate pick in "Colors on this product" -- render
    // that one, now.
    function onStageColor(sku) {
        if (disposed || !sku) return
        for (const [name, e] of entries) {
            if (e.sku !== sku) continue
            autoPicked.delete(sku)
            enqueue(name)
        }
        pump()
        publishMissing()
    }

    // TRIGGER 3: "Render all colors" -- every color that needs one, now.
    function renderAll() {
        if (disposed) return
        for (const [name, e] of entries) {
            autoPicked.delete(e.sku)
            enqueue(name)
        }
        pump()
        publishMissing()
    }

    function reconcile(next) {
        if (disposed) return
        // A different product: nothing carries over (a "Red" on product A
        // must never regenerate into product B's listing), and nothing it
        // was opened with renders until a trigger fires.
        if (campaignProductId.value !== productId) {
            resetAll()
            flushOnNextSave = false
            productId = campaignProductId.value
            autoPicked = new Set()
            recordedWake = new Map()
            recordingWake = new Map()
            serverWake = new Map()
            legacyRerendered = new Set()
            partialRetriesUsed = 0
        }
        // The product's recorded pointers can arrive with (or after) the
        // load; read them on every pass, never overriding a newer record.
        for (const [sku, rec] of loadRecordedPointers()) {
            if (!serverWake.has(sku)) serverWake.set(sku, rec.wake)
            if (!recordedWake.has(sku)) recordedWake.set(sku, rec.wake)
        }
        // A SYSTEM selection (the default-color pick) waits for the
        // distributor before the save debounce renders it.
        for (const sku of renderStore.consumeAutoSelectedColors?.() ?? []) {
            autoPicked.add(sku)
        }
        const selectedSkus = new Set(selectedColors.value.map((c) => c.color_sku))
        for (const sku of [...autoPicked]) {
            if (!selectedSkus.has(sku)) autoPicked.delete(sku)
        }
        for (const [name, e] of [...entries]) {
            if (name in next) continue
            // A deselected color starts fresh if it comes back: it needs a
            // trigger again (served free from cache when it has one).
            serverWake.delete(e.sku)
            clearEntry(name)
        }
        const legacyNames = []
        for (const [name, d] of Object.entries(next)) {
            const prev = entries.get(name)
            if (prev && prev.wake === d.wake) {
                // Same art on this color: follow the cache key, start nothing.
                prev.key = d.key
                prev.placements = d.args.groupPlacementCount ?? null
                continue
            }
            const prevUrl =
                prev?.status === 'ready' ? prev.url : (prev?.prevUrl ?? null)
            const current = !prev && serverWake.get(d.sku) === d.wake
            // A record that would be current but for the recipe version was
            // rendered the old way (#312): re-render it once, below.
            const legacy =
                !prev &&
                !current &&
                !legacyRerendered.has(d.sku) &&
                d.wake.startsWith(`${WAKE_RECIPE}:`) &&
                serverWake.get(d.sku) === d.wake.slice(WAKE_RECIPE.length + 1)
            let legacyPrevUrl = null
            if (legacy) {
                // The old (possibly partial) render sits in Chosen; the new
                // one replaces it in place.
                const chosen = editor.getEffectiveAttribute?.('storefront_images')?.[name]
                const old = Array.isArray(chosen)
                    ? chosen.find((e) => e && e.source === 'artifi' && e.url)
                    : null
                legacyPrevUrl = old?.url ?? null
                legacyRerendered.add(d.sku)
                legacyNames.push(name)
            }
            entries.set(name, {
                key: d.key,
                wake: d.wake,
                sku: d.sku,
                status: current ? 'ready' : 'needed',
                url: null,
                prevUrl: prevUrl ?? legacyPrevUrl,
                partialTries: 0,
                // What a clean render of this color draws (shown on its tile).
                placements: d.args.groupPlacementCount ?? null,
            })
            // A color that needs a render shows nothing yet -- no shimmer
            // until a trigger actually queues it.
            if (prev) renderStore.dropRender(name)
        }
        for (const name of legacyNames) enqueue(name)
        if (legacyNames.length > 0) pump()
        publishMissing()
    }

    renderStore.setRenderAllHandler(renderAll)
    renderStore.setRenderColorHandler(onStageColor)
    watch(desired, reconcile, { immediate: true })
    // The autosave state decides "scheduled" (see publishMissing).
    watch(() => editor.fieldAutoSaveState, () => {
        if (!disposed) publishMissing()
    })
    watch(() => editor.saveCount, (n, old) => {
        if (n === old) return
        onSaved()
        flushRecords({ saved: true })
    })
    // The server's selection moved (a save landed, or the product reloaded):
    // a record waiting for its color's selection may go now.
    watch(savedSelection, () => flushRecords())
    watch(() => editor.placementSaveCount, (n, old) => {
        if (n !== old) onPlacementSaved()
    })
    watch(() => editorUi.selectedLeftRailItem, (item, old) => {
        if (item !== old) onRailItem(item)
    })
    // A DELIBERATE pick renders now: a click in "Colors on this product"
    // (productEditorUiState intent 'pick', counted by colorPickCount).
    // The Stage's open-time pick and its re-pick after an untick move the
    // Stage but never trigger a render here.
    watch(() => editorUi.colorPickCount, (n, old) => {
        if (n !== old) onStageColor(editorUi.selectedColorSku)
    })
    // TICKING a color renders that ONE color now (Jake 2026-10-28), through
    // the same path and spend guards as a pick. An UNTICK carries the same
    // signal with the dropped sku, so only a sku that is still selected
    // renders (reconcile has already removed an unticked color's entry).
    watch(() => editorUi.colorTickCount, (n, old) => {
        if (n === old) return
        const sku = editorUi.selectedColorSku
        if (selectedColors.value.some((c) => c.color_sku === sku)) onStageColor(sku)
    })

    onScopeDispose(() => {
        disposed = true
        clearSaveTimer()
        clearPartialRetryTimer()
        entries.clear()
        pendingRecords.clear()
        queue = []
        // A newer instance may already own the shared store (an editor
        // remount): only the owner clears it.
        renderStore.clearRenderColorHandler(onStageColor)
        if (renderStore.clearRenderAllHandler(renderAll)) renderStore.reset()
    })
}

export default useStorefrontColorRenders
